import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import type { Colour, RecipientInput, SearchType } from '@tyro/api';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  RHFCheckboxGroup,
  useFormValidator,
} from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

export type MailRecipientType = {
  type: SearchType;
  label: string;
};

export type RecipientsForMailModal = Array<{
  name: string;
  id: RecipientInput['recipientPartyId'];
  type: 'individual' | 'group';
  avatarUrl?: string | null;
  avatarColor?: Colour | null;
}>;

interface SendMailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    parties: RecipientsForMailModal,
    selectedTypes: SearchType[],
  ) => Promise<void>;
  settings?: {
    parties: RecipientsForMailModal;
    possibleRecipientTypes: MailRecipientType[];
  };
}

interface MailFormState {
  recipientTypes: SearchType[];
}

export function SelectMailRecipientTypeModal({
  isOpen,
  onClose,
  onSubmit,
  settings,
}: SendMailModalProps) {
  const { t } = useTranslation(['common', 'mail']);
  const [isLoading, setIsLoading] = useState(false);
  const { resolver, rules } = useFormValidator<MailFormState>();
  const { reset, control, handleSubmit } = useForm<MailFormState>({
    resolver: resolver({
      recipientTypes: rules.required(),
    }),
  });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onSubmitRequest = handleSubmit(async ({ recipientTypes }) => {
    setIsLoading(true);
    await onSubmit(settings?.parties ?? [], recipientTypes);
    setIsLoading(false);
    reset();
  });

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      scroll="paper"
      fullWidth
      maxWidth="sm"
    >
      <form onSubmit={onSubmitRequest}>
        <DialogTitle>{t('mail:sendMail')}</DialogTitle>
        <DialogContent>
          <RHFCheckboxGroup<MailFormState, MailRecipientType>
            label={`${t('mail:sendTo')}:`}
            controlProps={{ name: 'recipientTypes', control }}
            options={settings?.possibleRecipientTypes ?? []}
            getOptionLabel={(option) => option.label}
            optionIdKey="type"
          />
        </DialogContent>
        <DialogActions>
          <Button variant="soft" onClick={onCancel}>
            {t('common:actions.cancel')}
          </Button>

          <Button loading={isLoading} type="submit" variant="contained">
            {t('common:actions.next')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
